<template>
  <b-container fluid>
    <b-row class="justify-content-center">
      <b-col cols="12" xl="2">
        <QuizInfo v-bind:quizID="quizID" class="mb-3" noTeam="true" />
        <b-card class="mb-3">
          <template v-slot:header>Manage Quiz</template>
          <div v-if="quiz">
            <div class="row" v-if="showStatusButton">
              <!-- ACTIVE QUIZ -->
              <div class="col-12 col-sm-3 col-md-2 col-xl-12 mb-3">
                <b-button
                  v-bind:variant="statusVariant"
                  block
                  @click="onStatusButton"
                  >{{ statusAction }}</b-button
                >
              </div>
              <div class="col-12 col-sm-9 col-md-10 col-xl-12 mb-2">
                <HostQuizItemControls
                  v-bind:quiz="quiz"
                  vertical="xl"
                  fullWidth="xl"
                />
              </div>
              <div class="col-12">
                <span
                  class="tq-hover-a tq-blue"
                  @click="manageTeamsVisible = true"
                  >Manage teams</span
                >
              </div>
              <!-- END ACTIVE QUIZ -->
            </div>
            <div v-else>Quiz finalized</div>
          </div>
        </b-card>
        <b-card class="mb-3">
          <template v-slot:header>Round stats</template>
          <b-alert v-if="!roundHistogramm || $_.size(roundHistogramm) == 0" show
            >No stats available.</b-alert
          >
          <div v-else-if="rounds">
            <div v-for="(round, key, index) in roundHistogramm" :key="index">
              <TopquizTippy
                noHeader="true"
                placement="left"
                :selector="'.roundhisto' + key"
              >
                <template v-slot:trigger>
                  <b-badge
                    class="w-100"
                    :class="'roundhisto' + key"
                    variant="dark"
                    >{{ rounds[index].name }}</b-badge
                  >
                </template>
                <div>
                  <p>
                    <round-histogramm-info :round="round" />
                  </p>
                </div>
              </TopquizTippy>
            </div>
            <div class="text-muted text-tiny text-center">
              Hover your mouse over a round to see the points statistics.
            </div>
          </div>
        </b-card>
        <b-card class="mb-3">
          <template v-slot:header>Surveys</template>
          <b-row>
            <b-col>
              <b-button
                variant="primary"
                block
                @click="onAskQuestionClick"
                class="mb-2"
                >Ask something</b-button
              >
              <HostQuestionButton
                v-for="q in surveys"
                :key="q.id"
                :question="q"
                :answers="answers[q.id]"
                class="mb-1"
              />
            </b-col>
          </b-row>
        </b-card>
      </b-col>
      <b-col cols="12" xl="10">
        <ScoreTable
          v-bind:quizID="quizID"
          :externalFields="externalFields"
          spectate="false"
          @row-contextmenu="rowRightClicked"
          @update:roundHistogramm="roundHistogramm = $event"
        />
      </b-col>
    </b-row>
    <DialogQuizStatus
      v-if="quiz"
      v-bind:quiz="quiz"
      v-model="quizStatusDialogVisible"
      @ok="statusChangeRequested"
    />
    <HostQuestionDialog
      :quizID="quizID"
      v-model="questionDialogVisible"
      @question-submit="questionSubmitted"
    />
    <HostManageTeamsDialog :quizID="quizID" v-model="manageTeamsVisible" />
    <DialogError v-model="errorVisible">{{ errorMessage }}</DialogError>
    <DialogYesNo v-model="confirmationVisible" @ok="confirmationOk">{{
      confirmationMessage
    }}</DialogYesNo>
    <SurveyAnswerDialog
      v-model="surveyAnswerVisible"
      :question="selectedQuestion"
      :rounds="rounds"
      :allowRandomRound="true"
      hostMode
      @answer-submit="submitAnswer"
    ></SurveyAnswerDialog>
    <ContextMenu
      :position="contextPosition"
      v-model="contextShow"
      :actions="contextActions"
      :actionData="contextItem"
    />
    <DialogInfo v-model="scoreDialogVisible">
      <QuizScoring
        ref="teamScoring"
        :scoreTeamID="scoreForTeamID"
        :quizID="quizID"
        :isHost="true"
      />
    </DialogInfo>
    <TopQuizChat :quizID="quizID" />
  </b-container>
</template>

<script>
import ContextMenu from "@/components/utils/ContextMenu";
import DialogInfo from "@/components/DialogInfo";
import DialogYesNo from "@/components/DialogYesNo";
import DialogError from "@/components/DialogError";
import QuizInfo from "@/components/QuizInfo";
import ScoreTable from "@/components/ScoreTable";
import TopQuizChat from "@/components/TopQuizChat";
import SurveyAnswerDialog from "@/components/SurveyAnswerDialog";
import DialogQuizStatus from "@/components/host/DialogQuizStatus";
import HostQuestionDialog from "@/components/host/HostQuestionDialog";
import HostQuestionButton from "@/components/host/HostQuestionButton";
import HostManageTeamsDialog from "@/components/host/HostManageTeamsDialog";
import HostQuizItemControls from "@/components/HostQuizItemControls";
import QuizScoring from "@/components/QuizScoring";
import explanations from "@/assets/explanations.js";
import RoundHistogrammInfo from "../components/host/RoundHistogrammInfo.vue";

export default {
  name: "HostGoQuiz",
  components: {
    QuizInfo,
    ScoreTable,
    HostQuizItemControls,
    HostQuestionDialog,
    DialogQuizStatus,
    HostQuestionButton,
    DialogError,
    DialogYesNo,
    HostManageTeamsDialog,
    SurveyAnswerDialog,
    ContextMenu,
    QuizScoring,
    DialogInfo,
    TopQuizChat,
    RoundHistogrammInfo
  },
  data() {
    return {
      quizID: 0,
      quiz: undefined,
      rounds: undefined,
      quizStatusDialogVisible: false,
      questionDialogVisible: false,
      surveys: [],
      answers: {},
      errorMessage: "",
      errorVisible: false,
      confirmationMessage: "",
      confirmationAction: null,
      confirmationVisible: false,
      manageTeamsVisible: false,
      surveyAnswerVisible: false,
      selectedQuestion: null,
      contextShow: false,
      contextPosition: [0, 0],
      contextActions: this.getContextActions(),
      contextItem: null,
      scoreDialogVisible: false,
      scoreForTeamID: null,
      roundHistogramm: null
    };
  },
  computed: {
    externalFields() {
      var fields = [];
      this.surveys.forEach(survey => {
        if (!survey.showInTable) {
          return;
        }

        fields.push({
          key: "ext_" + survey.id,
          label: survey.title,
          data: teamID => {
            if (!this.answers[survey.id]) {
              return null;
            }
            return this.answers[survey.id][teamID];
          }
        });
      });
      return fields;
    },
    showStatusButton() {
      if (!this.quiz) {
        return false;
      }
      return this.quiz.status < this.$tqConstants["QUIZ_STATUS_FINALIZED"];
    },
    statusAction() {
      if (!this.quiz) {
        return "";
      }
      return explanations.quiz_action_next[this.quiz.status];
    },
    statusVariant() {
      if (!this.quiz) {
        return "";
      }
      return explanations.quiz_action_next_variants[this.quiz.status];
    }
  },
  created() {
    this.quizID = this.$route.params.id;

    this.$topquiz.checkSession(() => {
      this.$topquiz.sendCommand(
        "host_getQuizData",
        this.$topquiz.SessionKey,
        this.quizID
      );
    });

    this.$topquiz.on(this, "host_getQuizData_success", params => {
      this.quiz = params[0];
      this.rounds = params[1];

      this.$topquiz.sendCommand(
        "host_getSurveyData",
        this.$topquiz.SessionKey,
        this.quizID
      );
    });

    this.$topquiz.on(this, "quizStatusChanged", params => {
      var quizID = params[0];
      if (this.quiz.id == quizID) {
        this.quiz.status = params[1];
      }
    });

    this.$topquiz.on(this, "host_getSurveyData_success", params => {
      this.surveys = params[0];

      this.$topquiz.sendCommand(
        "host_getSurveyAnswers",
        this.$topquiz.SessionKey,
        this.quizID
      );
    });

    this.$topquiz.on(this, "host_askQuestion_success", params => {
      this.surveys.push(params[0]);
    });

    this.$topquiz.on(this, "host_askQuestion_error", params => {
      this.errorMessage = params[0];
      this.errorVisible = true;
    });

    this.$topquiz.on(this, "answerReceived", params => {
      this.addAnswer(params[0]);
    });

    this.$topquiz.on(this, "surveyBatchAnswer", params => {
      var answers = params[1];
      answers.forEach(a => {
        this.addAnswer(a);
      });
    });

    this.$topquiz.on(this, "teamLeft", params => {
      var teamIDs = params[0];
      teamIDs.forEach(teamID => {
        Object.keys(this.answers).forEach(key => {
          var answers = this.answers[key];
          this.$delete(answers, teamID);
        });
      });
    });

    this.$topquiz.on(this, "host_getSurveyAnswers_success", params => {
      var answersByQuestion = params[0];
      Object.keys(answersByQuestion).forEach(key => {
        var answers = answersByQuestion[key];
        answers.forEach(teamAnswer => {
          this.addAnswer(teamAnswer);
        });
      });
    });

    this.$topquiz.on(this, "questionStatusChanged", params => {
      var question = params[0];

      this.surveys.forEach(survey => {
        if (survey.id == question.id) {
          survey.active = question.active;
        }
      });
    });

    this.$topquiz.on(this, "questionListedChanged", params => {
      var question = params[0];
      this.surveys.forEach(survey => {
        if (survey.id == question.id) {
          survey.showInTable = question.showInTable;
        }
      });
    });

    this.$topquiz.on(this, "host_setQuestionActive_success", params => {
      var question = params[0];
      if (!question.active) {
        this.confirmationMessage =
          "You have just disabled a survey. Do you want to set an answer for teams that didn't?";
        this.confirmationVisible = true;
        this.confirmationAction = "answerForBlankTeams";
        this.selectedQuestion = question;
      }
    });
  },
  destroyed() {
    this.$topquiz.unsubscribe(this);
  },
  methods: {
    confirmationOk() {
      if (this.confirmationAction) {
        eval("this." + this.confirmationAction + "();");
      }
    },
    answerForBlankTeams() {
      if (!this.selectedQuestion) {
        return;
      }

      this.surveyAnswerVisible = true;
    },
    submitAnswer(answer) {
      this.$topquiz.sendCommand(
        "host_answerForBlankTeams",
        this.$topquiz.SessionKey,
        answer.question_id,
        answer.answer
      );
    },
    addAnswer(answer) {
      if (!this.answers[answer.question_id]) {
        this.$set(this.answers, answer.question_id, {});
      }

      this.$set(
        this.answers[answer.question_id],
        answer.team_id,
        answer.answer
      );
    },
    onStatusButton() {
      this.quizStatusDialogVisible = true;
    },
    statusChangeRequested(_, nextStatus) {
      this.$topquiz.sendCommand(
        "host_changeQuizStatus",
        this.$topquiz.SessionKey,
        this.quiz.id,
        nextStatus
      );
    },
    onAskQuestionClick() {
      this.questionDialogVisible = true;
    },
    questionSubmitted(question) {
      this.$topquiz.sendCommand(
        "host_askQuestion",
        this.$topquiz.SessionKey,
        question
      );
    },
    rowRightClicked(rowItem, event) {
      event.preventDefault();
      // var team = rowItem.team;
      this.contextPosition = [event.x, event.y];
      this.contextItem = rowItem;
      this.contextShow = true;
    },
    getContextActions() {
      return [
        {
          id: 0,
          title: "Make virgin team",
          hidden: x => x.team.virgin == 1,
          onClick: x => this.setVirginTeam(x.team, true)
        },
        {
          id: 1,
          title: "Remove virgin status",
          hidden: x => x.team.virgin == 0,
          onClick: x => this.setVirginTeam(x.team, false)
        },
        {
          id: 2,
          title: "Make offline team",
          hidden: x => x.team.offlineTeam == 1,
          onClick: x => this.setOfflineTeam(x.team, true)
        },
        {
          id: 3,
          title: "Remove offline status",
          hidden: x => x.team.offlineTeam == 0,
          onClick: x => this.setOfflineTeam(x.team, false)
        },
        {
          id: 4,
          title: "Edit team score",
          onClick: x => this.scoreForTeam(x.team)
        }
      ];
    },
    setVirginTeam(team, virgin) {
      this.$topquiz.sendCommand(
        "host_setVirginTeam",
        this.$topquiz.SessionKey,
        this.quiz.id,
        team.team_id,
        virgin
      );
    },
    setOfflineTeam(team, offline) {
      this.$topquiz.sendCommand(
        "setOfflineTeam",
        this.$topquiz.SessionKey,
        this.quiz.id,
        team.team_id,
        offline
      );
    },
    scoreForTeam(team) {
      this.scoreForTeamID = team.team_id;
      this.scoreDialogVisible = true;
      this.$nextTick(() => {
        this.$refs.teamScoring.setQuiz(this.quiz, this.rounds);
        this.$topquiz.sendCommand(
          "getMyScore",
          this.$topquiz.SessionKey,
          this.scoreForTeamID,
          this.quiz.id
        );
      });
    }
  }
};
</script>
